import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Dot } from "./thUi";
import { Row } from "./THRow";
import { daySummary, fmtDayHead, fmtTime, at, isOpen, needsYou, dayKey, addDays, startOfDay, initials, AMBER, INDIGO, GREEN, FAINT, GREY2, INK } from "./thModel";
import { displayName } from "@/lib/patientFormat";

const RED = "#D93025";
export function Summary({ s }) {
  const P = (n, kind, color, t) => (n ? <span className="inline-flex items-center gap-1.5" style={{ color }}><Dot kind={kind} sm />{n} {t}</span> : null);
  return <span className="flex flex-wrap items-center gap-3 text-[12px]">{P(s.need, "amber", AMBER, "need you")}{P(s.open, "indigo", INDIGO, "open")}{P(s.done, "green", GREEN, "completed")}{P(s.gone, "grey", FAINT, "cancelled or no-show")}</span>;
}

function NowLine() {
  return (
    <div className="grid items-center" style={{ gridTemplateColumns: "64px 24px 1fr", padding: "0 16px", height: 18 }} data-testid="th-nowline">
      <span className="text-[11px] font-semibold text-right pr-1 tabular-nums" style={{ color: RED }}>{fmtTime(new Date())}</span>
      <span className="flex justify-center"><span className="w-[9px] h-[9px] rounded-full" style={{ background: RED }} /></span>
      <span style={{ height: 1.5, background: RED }} />
    </div>
  );
}

function Fold({ label, done, gone, open, onToggle, testid }) {
  return (
    <div className="grid items-center" style={{ gridTemplateColumns: "64px 24px 1fr", padding: "0 16px", minHeight: 40 }} data-testid={testid}>
      <span />
      <span className="th-rail !self-stretch items-center"><span className="relative w-[7px] h-[7px] rounded-full" style={{ background: "#D4D4DA" }} /></span>
      <span className="flex flex-wrap items-center gap-3 text-[12.5px] pl-1" style={{ color: GREY2 }}>
        {open ? null : <span className="font-semibold">{label}</span>}
        {!open && done ? <span className="inline-flex items-center gap-1.5"><Dot kind="green" sm />{done} completed</span> : null}
        {!open && gone ? <span className="inline-flex items-center gap-1.5"><Dot kind="grey" sm />{gone} cancelled or no-show</span> : null}
        <button type="button" className="font-semibold" style={{ color: "#4A4AC2" }} onClick={onToggle} data-testid={`${testid}-toggle`}>{open ? "Hide earlier visits" : "Show"}</button>
      </span>
    </div>
  );
}

function DayBody({ day, items, seg, rowProps, compact }) {
  const [showEarly, setShowEarly] = useState(false), [showLate, setShowLate] = useState(false);
  const now = new Date(), past = day.key < dayKey(now), folds = seg === "open";
  const early = folds ? items.filter((a) => !isOpen(a) && (past || at(a) < now)) : [];
  const late = folds ? items.filter((a) => !isOpen(a) && !past && at(a) >= now) : [];
  const shown = folds ? items.filter((a) => isOpen(a) || (showEarly && early.includes(a)) || (showLate && late.includes(a))) : items;
  const nowAt = day.isToday && ["open", "waiting", "all"].includes(seg) ? shown.findIndex((a) => at(a) >= now) : -2;
  const out = [];
  if (early.length) out.push(<Fold key="early" testid={`th-fold-${day.key}`} open={showEarly} onToggle={() => setShowEarly(!showEarly)} label={past ? `${early.length} finished` : `${early.length} earlier today`} done={early.filter((a) => a.status === "completed").length} gone={early.filter((a) => a.status !== "completed").length} />);
  shown.forEach((a, i) => { if (i === nowAt) out.push(<NowLine key="now" />); out.push(rowProps(a, compact)); });
  if (nowAt === -1 && shown.length) out.push(<NowLine key="now" />);
  if (day.isToday && nowAt !== -2 && !shown.length) out.push(<NowLine key="now" />);
  if (late.length && !showLate) out.push(
    <div key="late" className="text-[12.5px] py-2" style={{ paddingLeft: 108, color: GREY2 }} data-testid={`th-late-${day.key}`}>
      {late.length} cancelled or no-show visit{late.length > 1 ? "s" : ""} {day.isToday ? "later today " : ""}hidden · <button type="button" className="font-semibold" style={{ color: "#4A4AC2" }} onClick={() => setShowLate(true)} data-testid={`th-late-${day.key}-show`}>Show</button>
    </div>,
  );
  return <div className="pb-2">{out}</div>;
}

export const Avatar = ({ name, size = 28 }) => <span className="inline-flex items-center justify-center rounded-full flex-none text-[11px] font-semibold" style={{ width: size, height: size, background: INK, color: "#fff" }}>{initials(name)}</span>;

function ProvHead({ p, items, blur, right }) {
  return (
    <div className={`flex items-center gap-2 px-4 py-2 border-b ${blur ? "th-blur" : ""}`} style={{ borderColor: "#F1F1F4" }} data-testid={`th-colhead-${p.npi}`}>
      <Avatar name={p.name} /><span className="text-[12.5px] font-semibold truncate">{displayName(p.name)}</span>
      <span className="ml-auto flex items-center gap-2"><Summary s={daySummary(items)} />{right}</span>
    </div>
  );
}

function Strip({ p, items, onClick }) {
  const need = items.filter(needsYou).length, open = items.filter((a) => isOpen(a) && !needsYou(a)).length;
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-2 py-3 border-r h-full hover:bg-black/[.02]" style={{ borderColor: "#F1F1F4" }} aria-label={`Show ${displayName(p.name)}`} data-testid={`th-strip-${p.npi}`}>
      <Avatar name={p.name} />
      {need ? <span className="text-[11px] font-semibold" style={{ color: AMBER }}>{need}</span> : null}
      {open ? <span className="text-[11px] font-semibold" style={{ color: INDIGO }}>{open}</span> : null}
      <span className="text-[11.5px] font-semibold" style={{ writingMode: "vertical-rl", color: GREY2 }}>{displayName(p.name)}</span>
    </button>
  );
}

function ProvidersBody({ d, seg, rowProps, provs, expId, onAll }) {
  const by = (p) => d.items.filter((a) => String(a.npi) === p.npi);
  if (provs.length > 4) return provs.filter((p) => by(p).length).map((p) => (
    <div key={p.npi} data-testid={`th-stack-${p.npi}`}><ProvHead p={p} items={by(p)} blur={!!expId} /><DayBody day={d} items={by(p)} seg={seg} rowProps={rowProps} /></div>
  ));
  const focus = d.items.find((a) => a.appointment_id === expId)?.npi;
  const cols = provs.map((p) => (focus ? (String(focus) === p.npi ? "minmax(0,1fr)" : "64px") : "minmax(340px,1fr)")).join(" ");
  return (
    <div className="overflow-x-auto" data-testid={`th-cols-${d.key}`}>
      <div className="grid" style={{ gridTemplateColumns: cols }}>
        {provs.map((p) => (focus && String(focus) !== p.npi ? <Strip key={p.npi} p={p} items={by(p)} onClick={onAll} /> : (
          <div key={p.npi} className="min-w-0 border-r last:border-r-0" style={{ borderColor: "#F1F1F4" }} data-testid={`th-col-${p.npi}`}>
            <ProvHead p={p} items={by(p)} right={focus ? <button type="button" className="th-btn sec !h-7" onClick={onAll} data-testid="th-all-doctors">All doctors</button> : null} />
            <DayBody day={d} items={by(p)} seg={seg} rowProps={rowProps} compact={!focus} />
          </div>
        )))}
      </div>
    </div>
  );
}

export function DayList({ days, seg, rowProps, blurOthers, provs = [], expId, onAll }) {
  const yest = dayKey(addDays(startOfDay(new Date()), -1));
  const [flip, setFlip] = useState(() => new Set());
  if (!days.length) return <div className="py-16 text-center text-[13px]" style={{ color: FAINT }} data-testid="th-empty">No visits in this view.</div>;
  return days.map((d) => {
    const def = d.key < yest, collapsed = flip.has(d.key) ? !def : def;
    const docs = new Set(d.items.map((a) => a.npi)).size;
    return (
      <section key={d.key} className="border-b last:border-b-0" style={{ borderColor: "#F1F1F4" }} data-testid={`th-day-${d.key}`}>
        <button type="button" className="w-full flex items-center gap-2 px-4 py-3 text-left" aria-expanded={!collapsed}
          onClick={() => setFlip((s) => { const n = new Set(s); if (n.has(d.key)) n.delete(d.key); else n.add(d.key); return n; })} data-testid={`th-dayhead-${d.key}`}>
          <ChevronRight size={15} style={{ transform: collapsed ? "none" : "rotate(90deg)", transition: "transform .15s", color: GREY2 }} />
          <span className="text-[13.5px] font-semibold">{fmtDayHead(d.date)}</span>
          {d.isToday ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-[5px] border" style={{ borderColor: "#D4D4DA", color: GREY2 }}>Today</span>
            : d.key === dayKey(addDays(new Date(), 1)) ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-[5px] border" style={{ borderColor: "#E9E9EC", color: GREY2 }} data-testid={`th-tomorrow-${d.key}`}>Tomorrow</span> : null}
          <span className="text-[12px]" style={{ color: FAINT }}>{d.items.length} visit{d.items.length === 1 ? "" : "s"}{docs > 1 ? ` · ${docs} doctors` : ""}</span>
          <span className="ml-auto"><Summary s={daySummary(d.items)} /></span>
        </button>
        {collapsed ? null : provs.length > 1 ? <ProvidersBody d={d} seg={seg} rowProps={rowProps} provs={provs} expId={expId} onAll={onAll} /> : <DayBody day={d} items={d.items} seg={seg} rowProps={rowProps} />}
      </section>
    );
  });
}

export { Row };
